import { getPayerRequirements, getIrsLetter, getCredVerification } from 'api/credentialing';
import type { IrsLetterDto, PayerRequirementsDto, TSelectedPayer } from 'components/CredentialingPayerSteps';
import {
    EPayerStep,
    govComplete,
    serviceAddressComplete,
    additionalW9Complete,
    aetnaComplete,
    getApplicablePayerSteps,
    selectedStates,
    optumTriggered,
    govTriggered,
    irsLetterTriggered,
    irsLetterComplete,
} from 'components/CredentialingPayerSteps';

// Medicaid enrollment（PAVE 问卷）不是链里的一步，只在 Payer requirements 里有单独的板块
export const MEDICAID_SECTION = 'medicaidEnrollment';
export type TSectionKey = EPayerStep | typeof MEDICAID_SECTION;

export const PAYER_SECTION_TITLES: Record<TSectionKey, string> = {
    [EPayerStep.NP_104]: 'California 104 NP designation',
    [EPayerStep.SERVICE_ADDRESS]: 'Service address by state',
    [EPayerStep.GOV_PROGRAM]: 'Government program details',
    [MEDICAID_SECTION]: 'Medicaid enrollment',
    [EPayerStep.IRS_LETTER]: 'IRS Tax ID confirmation letter',
    [EPayerStep.AETNA_FOCUS]: 'Aetna Practice Focus Areas',
    [EPayerStep.OPTUM_W9]: 'UnitedHealthcare W-9',
    [EPayerStep.MEDICARE_PAYOUT]: 'Payout account',
};

export type TIrsState = { letter: IrsLetterDto; billsUnderTaxId: boolean };
export type TMcdCtx = { entityType?: string; licenseActionNo: boolean };

export type TPayerReqData = { reqs: PayerRequirementsDto; irs: TIrsState; mcdCtx: TMcdCtx };

const digits = (v?: string) => (v || '').replace(/\D/g, '');

// 一个 provider 的 payer requirements + IRS letter + Verification 衍生字段（panel 和 review Submit 校验共用）
export const loadPayerReqData = async (
    providerId: number | string,
    isGroupPractice: boolean,
): Promise<TPayerReqData> => {
    const [res, irsRes, verRes]: any[] = await Promise.all([
        getPayerRequirements(providerId),
        getIrsLetter(providerId).catch(() => null),
        getCredVerification(providerId).catch(() => null),
    ]);
    const ver = verRes?.data?.data;
    const tin = digits(ver?.taxId);
    const ssn = digits(ver?.ssn);
    const licenseAction = (ver?.disclosures || []).find((d: any) => d?.questionKey === 'LICENSE_ACTION');
    return {
        reqs: res?.data?.data || {},
        irs: {
            letter: irsRes?.data?.data || {},
            billsUnderTaxId: isGroupPractice || (tin.length === 9 && tin !== ssn),
        },
        mcdCtx: {
            entityType: ver?.businessEntityType || undefined,
            licenseActionNo: licenseAction?.answer === false,
        },
    };
};

const uniq = (arr: string[]) => Array.from(new Set(arr));

// 各板块的州列表 / 是否该出现：按「选中 payer 的触发计算 ∪ 已存数据」（与 onboarding / Add payers 链同一套触发函数）。
// 新加了 payer 但还没存过 requirements 的州也会出现；已存数据里有、但 payer 已不触发的州继续展示，不丢数据
export const payerSectionScope = (payers: TSelectedPayer[], reqs: PayerRequirementsDto, irs: TIrsState) => {
    const triggerStates = selectedStates(payers);
    return {
        svcStates: uniq([...triggerStates, ...(reqs.serviceAddresses || []).map((a) => a.state)]),
        govStates: uniq([
            ...(govTriggered(payers) ? triggerStates : []),
            ...(reqs.medicare || []).map((g) => g.state),
            ...(reqs.medicaid || []).map((g) => g.state),
        ]),
        aetnaShow: getApplicablePayerSteps(payers).includes(EPayerStep.AETNA_FOCUS) || !!reqs.focusAreas?.length,
        w9Show: optumTriggered(payers) || !!reqs.additionalW9?.items?.length,
        irsShow:
            (irs.billsUnderTaxId && irsLetterTriggered(payers)) ||
            !!irs.letter.fileUrl ||
            !!(irs.letter.legalName || '').trim(),
    };
};

// Review Submit：该出现但没填完的板块（按页面顺序）。Medicaid 问卷不拦（原型：缺的由 ops 跟进）
export const payerRequirementGaps = (
    payers: TSelectedPayer[],
    data: Pick<TPayerReqData, 'reqs' | 'irs'>,
): TSectionKey[] => {
    const { reqs, irs } = data;
    const scope = payerSectionScope(payers, reqs, irs);
    const gaps: TSectionKey[] = [];
    if (scope.svcStates.length && !serviceAddressComplete(scope.svcStates, reqs.serviceAddresses || [])) {
        gaps.push(EPayerStep.SERVICE_ADDRESS);
    }
    if (
        scope.govStates.length &&
        !govComplete(scope.govStates, reqs.medicare || [], reqs.medicaid || [], reqs.medicareManagement ?? null)
    ) {
        gaps.push(EPayerStep.GOV_PROGRAM);
    }
    if (scope.irsShow && !irsLetterComplete(irs.letter)) gaps.push(EPayerStep.IRS_LETTER);
    if (scope.aetnaShow && !aetnaComplete(reqs.focusAreas || [])) gaps.push(EPayerStep.AETNA_FOCUS);
    if (scope.w9Show && !additionalW9Complete(reqs.additionalW9)) gaps.push(EPayerStep.OPTUM_W9);
    return gaps;
};
